// Resolve an EPSG code (e.g. "EPSG:3763") to a proj4 definition string.
//
// Por esta ordem:
//   1. as zonas UTM (WGS 84 e ETRS89), que se escrevem pela regra;
//   2. o índice EPSG incluído (epsgIndice.json, ~6400 CRS projectados,
//      geográficos e compostos), gerado da base de dados do PROJ por
//      scripts/gerar-indice-epsg.py: a definição, a área de uso, a unidade
//      dos eixos e a transformação para WGS 84 que se usou, com a precisão;
//   3. o epsg.io, pela rede, para o que não estiver no índice.
// Null quer dizer "CRS desconhecido"; o painel do mapa mostra isso em vez
// de adivinhar.
//
// Grelhas de mudança de datum: para os datums que têm uma das grelhas
// incluídas (vendor/grelhas, ver scripts/obter-grelhas.mjs), resolverNoPonto
// usa a grelha quando o ponto está dentro dela (precisão de 1 a 10 cm na
// maior parte, contra cerca de 1 m do Helmert). Fora da grelha fica o
// Helmert: o proj4js, sem grelha para o ponto, não mudava o datum.
//
// Porque não as definições do epsg.io para tudo: conferido contra o PROJ
// completo, o epsg.io dá ao Datum 73 (EPSG:27493) as rotações com o sinal
// trocado, 50 m ao lado em Lisboa. O índice usa o pipeline do PROJ.

const cache = new Map<string, string | null>()

/** Uma entrada do índice: [nome, proj4, [oeste, sul, este, norte],
 *  metros por unidade dos eixos, transformação para WGS 84 ("" = nenhuma
 *  de Helmert conhecida), grelha de mudança de datum ou null,
 *  transformação por grelha, motivo por que o proj4 não o reproduz ou
 *  null]. */
type EntradaDoIndice = [
  string,
  string,
  [number, number, number, number] | null,
  number,
  string,
  (string | null)?,
  string?,
  (string | null)?,
]

export interface InfoDoCrs {
  codigo: string
  nome: string
  /** Área de uso em graus: oeste, sul, este, norte. */
  area: [number, number, number, number] | null
  metros: number
  /** A transformação para WGS 84 usada, com a precisão; "" quando o PROJ
   *  não tem nenhuma de Helmert para este datum. */
  transformacao: string
  /** A grelha de mudança de datum que o PROJ usa para este datum, se for
   *  uma das incluídas, e a descrição dessa transformação. */
  grelha: string | null
  transformacaoGrelha: string
  /** Quando o proj4 não reproduz este CRS (projecção que não tem, ou
   *  parâmetros que ignora), o motivo; o mapa não o desenha. */
  naoSuportado: string | null
  /** CRS geográfico (latitude e longitude em graus): não serve de alvo a
   *  uma IfcMapConversion, que pede coordenadas de uma projecção. */
  geografico: boolean
}

let indice: Record<string, EntradaDoIndice> | null = null
let aCarregar: Promise<Record<string, EntradaDoIndice>> | null = null

/** Carrega o índice (só quem usa o mapa o paga: ~190 kB comprimido). */
export function carregarIndiceEpsg(): Promise<Record<string, EntradaDoIndice>> {
  if (indice) return Promise.resolve(indice)
  aCarregar ??= import('./epsgIndice.json').then((m) => {
    const dados = (m as unknown as { default?: { crs: Record<string, EntradaDoIndice> }; crs?: Record<string, EntradaDoIndice> })
    indice = (dados.default ?? dados).crs ?? {}
    return indice
  })
  return aCarregar
}

/** As zonas UTM têm uma definição que se escreve pela regra (zona e
 *  hemisfério): WGS 84 (326zz norte, 327zz sul) e ETRS89 (258zz, 28 a 38
 *  norte). */
function defUtm(epsg: string): string | null {
  const n = /^EPSG:(\d+)$/.exec(epsg)
  if (!n) return null
  const c = Number(n[1])
  if (c >= 32601 && c <= 32660) return `+proj=utm +zone=${c - 32600} +datum=WGS84 +units=m +no_defs`
  if (c >= 32701 && c <= 32760) return `+proj=utm +zone=${c - 32700} +south +datum=WGS84 +units=m +no_defs`
  if (c >= 25828 && c <= 25838) {
    return `+proj=utm +zone=${c - 25800} +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs`
  }
  return null
}

/** O código WGS 84 / UTM da zona onde cai uma longitude e latitude
 *  (regra simples das zonas de 6 graus; as excepções da Noruega e de
 *  Svalbard não se aplicam aqui). */
export function epsgUtmWgs84(latitude: number, longitude: number): string {
  const zona = Math.min(60, Math.max(1, Math.floor((longitude + 180) / 6) + 1))
  return `EPSG:${(latitude >= 0 ? 32600 : 32700) + zona}`
}

/** As formas em que um código EPSG aparece no Name de um CRS: "3763",
 *  "EPSG:3763", "EPSG::3763", "urn:ogc:def:crs:EPSG::3763" (ou com versão,
 *  "urn:ogc:def:crs:EPSG:9.8:3763") e as compostas "EPSG:3763+5780" ou
 *  "EPSG:25832+EPSG:5783". Devolve [horizontal, vertical], ou null. */
function partesDoCodigo(code: string): [string, string | null] | null {
  const v = code.trim()
  const urn = /^URN:OGC:DEF:CRS:EPSG:[^:]*:(\d+)$/i.exec(v)
  if (urn) return [urn[1], null]
  const m = /^(?:EPSG\s*:{1,2}\s*)?(\d+)\s*(?:\+\s*(?:EPSG\s*:{1,2}\s*)?(\d+))?$/i.exec(v)
  return m ? [m[1], m[2] ?? null] : null
}

/** Normalise "3763" / "epsg:3763" → "EPSG:3763". Num CRS composto fica a
 *  parte horizontal (a vertical vem em parteVerticalDoCrs). */
export function normalizeEpsg(code: string): string {
  const p = partesDoCodigo(code)
  if (p) return `EPSG:${p[0]}`
  const v = code.trim()
  return v.toUpperCase().startsWith('EPSG:')
    ? `EPSG:${v.slice(5).trim()}`
    : v
}

/** A parte vertical de um nome composto ("EPSG:3763+5780" → "EPSG:5780"),
 *  ou null. Só informação: o mapa e o globo usam a horizontal. */
export function parteVerticalDoCrs(code: string | null | undefined): string | null {
  const p = code ? partesDoCodigo(code) : null
  return p?.[1] ? `EPSG:${p[1]}` : null
}

/** A definição com os eixos em metros. O viewer trabalha sempre em metros
 *  (a leitura do IFC converte a origem pela unidade do mapa), por isso uma
 *  definição em pés (+units=us-ft, +to_meter=...) recebia metros como se
 *  fossem pés e punha o modelo centenas de km ao lado. No proj4 o x_0 e o
 *  y_0 já estão em metros: basta pôr +units=m. As definições em metros
 *  ficam iguais, carácter a carácter. */
export function definicaoEmMetros(def: string): string {
  const unidades = /\+units=(\S+)/.exec(def)?.[1]
  const toMeter = /\+to_meter=/.test(def)
  if (!toMeter && (unidades === undefined || unidades === 'm')) return def
  if (/\+proj=longlat\b/.test(def)) return def
  const d = def.replace(/\s*\+to_meter=\S+/g, '')
  return /\+units=\S+/.test(d) ? d.replace(/\+units=\S+/, '+units=m') : `${d} +units=m`
}

function numeroDe(epsg: string): string | null {
  const n = /^EPSG:(\d+)$/.exec(epsg)
  return n ? n[1] : null
}

/** O que o índice sabe de um CRS, ou null (fora do índice, ou não EPSG). */
export async function infoDoCrs(code: string | null | undefined): Promise<InfoDoCrs | null> {
  if (!code) return null
  const epsg = normalizeEpsg(code)
  const num = numeroDe(epsg)
  if (!num) return null
  const e = (await carregarIndiceEpsg())[num]
  if (!e) return null
  return {
    codigo: epsg,
    nome: e[0],
    area: e[2],
    metros: e[3],
    transformacao: e[4],
    grelha: e[5] ?? null,
    transformacaoGrelha: e[6] ?? '',
    naoSuportado: e[7] ?? null,
    geografico: /^\+proj=longlat(\s|$)/.test(e[1]),
  }
}

/** A definição proj4 do CRS, sempre com os eixos em METROS (ver
 *  definicaoEmMetros), ou null quando o CRS é desconhecido ou o proj4 não
 *  o reproduz (infoDoCrs diz porquê). */
export async function resolveProj4Def(
  code: string,
): Promise<string | null> {
  const def = await resolveProj4DefNativa(code)
  return def ? definicaoEmMetros(def) : null
}

/** A definição tal como vem do índice ou do epsg.io, nas unidades dos
 *  eixos do CRS. A cache guarda esta, para metrosDoCrsConhecido. */
async function resolveProj4DefNativa(
  code: string,
): Promise<string | null> {
  const epsg = normalizeEpsg(code)
  if (cache.has(epsg)) return cache.get(epsg) ?? null
  const utm = defUtm(epsg)
  if (utm) {
    cache.set(epsg, utm)
    return utm
  }
  const num = numeroDe(epsg)
  if (!num) {
    cache.set(epsg, null)
    return null
  }
  try {
    const e = (await carregarIndiceEpsg())[num]
    if (e) {
      const def = e[7] ? null : e[1]
      cache.set(epsg, def)
      return def
    }
  } catch {
    // Sem o índice (falhou o carregamento do pedaço): segue para a rede.
  }
  // epsg.io serve a definição proj4 em texto puro com CORS aberto.
  try {
    const res = await fetch(`https://epsg.io/${num}.proj4`, { mode: 'cors' })
    if (!res.ok) {
      cache.set(epsg, null)
      return null
    }
    const text = (await res.text()).trim()
    const def = text.startsWith('+') ? text : null
    cache.set(epsg, def)
    return def
  } catch {
    // Sem rede (modo local offline): não cachear, para poder tentar
    // de novo quando a ligação voltar.
    return null
  }
}

function metrosDaDefinicao(def: string): number | null {
  const unidades = /\+units=([a-z-]+)/.exec(def)?.[1]
  if (unidades === 'm') return 1
  if (unidades === 'ft') return 0.3048
  if (unidades === 'us-ft') return 1200 / 3937
  if (unidades === 'km') return 1000
  const toMeter = /\+to_meter=([0-9.eE+-]+)/.exec(def)?.[1]
  return toMeter ? Number(toMeter) : unidades === undefined ? 1 : null
}

/** Metros por unidade dos eixos de um CRS que já se conhece sem esperar
 *  (UTM, índice já carregado, ou trazido do epsg.io nesta sessão), ou null. */
export function metrosDoCrsConhecido(code: string | null | undefined): number | null {
  if (!code) return null
  const epsg = normalizeEpsg(code)
  const num = numeroDe(epsg)
  const e = num && indice ? indice[num] : undefined
  if (e) return e[3]
  const def = defUtm(epsg) ?? cache.get(epsg) ?? null
  return def ? metrosDaDefinicao(def) : null
}

/** Como metrosDoCrsConhecido, mas carrega o índice se for preciso. Não
 *  vai à rede. */
export async function metrosDoCrs(code: string | null | undefined): Promise<number | null> {
  if (!code) return null
  try {
    await carregarIndiceEpsg()
  } catch {
    // sem índice: o que já se sabe
  }
  return metrosDoCrsConhecido(code)
}

/** Um ponto (lon, lat) está na área de uso do CRS, com folga de meio grau? */
export function dentroDaArea(area: InfoDoCrs['area'], lon: number, lat: number): boolean | null {
  if (!area || !Number.isFinite(lon) || !Number.isFinite(lat)) return null
  const [w, s, e, n] = area
  const f = 0.5
  const dentroLon = w <= e ? lon >= w - f && lon <= e + f : lon >= w - f || lon <= e + f
  return dentroLon && lat >= s - f && lat <= n + f
}

// ---------------------------------------------------------------------
// Grelhas de mudança de datum
// ---------------------------------------------------------------------

interface GrelhaRegistada {
  nome: string
  ficheiro: string
  extensao: [number, number, number, number]
  descricao: string
  licenca: string
  credito: string
}

let manifesto: Promise<GrelhaRegistada[]> | null = null
function grelhasRegistadas(): Promise<GrelhaRegistada[]> {
  manifesto ??= import('../../../vendor/grelhas/grelhas.json').then(
    (m) => ((m as unknown as { default?: { grelhas: GrelhaRegistada[] } }).default ?? (m as unknown as { grelhas: GrelhaRegistada[] })).grelhas,
  )
  return manifesto
}

const grelhasCarregadas = new Map<string, Promise<boolean>>()

/** Carrega a grelha (servida pela nossa origem) e regista-a no proj4. */
export function carregarGrelha(nome: string, ficheiro: string): Promise<boolean> {
  let p = grelhasCarregadas.get(nome)
  if (!p) {
    p = (async () => {
      const r = await fetch(`/vendor/grelhas/${ficheiro}`)
      if (!r.ok) return false
      const [{ fromArrayBuffer }, { loadProj4 }] = await Promise.all([import('geotiff'), import('./proj4Lazy')])
      const tiff = await fromArrayBuffer(await r.arrayBuffer())
      const proj4 = await loadProj4()
      await (proj4.nadgrid(nome, tiff as never) as unknown as { ready: Promise<unknown> }).ready
      return true
    })().catch(() => false)
    grelhasCarregadas.set(nome, p)
    void p.then((ok) => {
      if (!ok) grelhasCarregadas.delete(nome)
    })
  }
  return p
}

export interface ResolucaoNoPonto {
  def: string
  /** A transformação para WGS 84 efectivamente usada (com a precisão). */
  transformacao: string
  grelha: string | null
}

/** A definição a usar para um modelo cuja âncora está em `ponto` (E, N
 *  no CRS): com a grelha de mudança de datum quando o ponto está dentro
 *  dela, senão a do índice (Helmert). */
export async function resolverNoPonto(code: string, ponto: [number, number] | null): Promise<ResolucaoNoPonto | null> {
  const base = await resolveProj4Def(code)
  if (!base) return null
  const info = await infoDoCrs(code).catch(() => null)
  const helmert: ResolucaoNoPonto = { def: base, transformacao: info?.transformacao ?? '', grelha: null }
  if (!info?.grelha || !ponto) return helmert
  try {
    const registo = (await grelhasRegistadas()).find((g) => g.nome === info.grelha)
    if (!registo) return helmert
    const { loadProj4 } = await import('./proj4Lazy')
    const proj4 = await loadProj4()
    const [lon, lat] = proj4(base, 'WGS84', ponto)
    const [w, s, e, n] = registo.extensao
    const m = 0.01
    if (!(lon > w + m && lon < e - m && lat > s + m && lat < n - m)) return helmert
    if (!(await carregarGrelha(registo.nome, registo.ficheiro))) return helmert
    const def = `${base.replace(/\s*\+towgs84=\S+/, '').replace(/\s*\+nadgrids=\S+/, '')} +nadgrids=${registo.nome}`
    // Conferir que a grelha cobre mesmo o ponto (há grelhas com buracos).
    const [lon2, lat2] = proj4(def, 'WGS84', ponto)
    if (!Number.isFinite(lon2) || !Number.isFinite(lat2) || Math.hypot(lon2 - lon, lat2 - lat) > 0.01) return helmert
    return { def, transformacao: `${info.transformacaoGrelha} (grelha ${registo.nome})`, grelha: registo.nome }
  } catch {
    return helmert
  }
}
